import { CSSResultGroup, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import componentStyles from './tufts-sidebar.css.lit';
import { TuftsMenu } from '../tufts-menu/tufts-menu';
import { MenuElementInterface } from 'decoupled-menu-parser-es6';

@customElement('tufts-sidebar')
export class TuftsSidebar extends TuftsMenu {
  static styles: CSSResultGroup = [componentStyles];

  @state() hamburgerSideBarOpen: boolean;

  // Finds button above current if exists and returns the button
  findParentButton(currentButton: Element) {
    const liGrandfather = currentButton.closest('ul')?.closest('li');
    if (liGrandfather) {
      return liGrandfather.querySelector('button');
    } else {
      return null;
    }
  }

  renderMenuItem(item: MenuElementInterface, menuDepth: number) {
    const title = item.link.attributes.title;
    const href = item.link.href === '' ? '#' : item.link.href;
    const children = item.children;

    if (!this.topMenuNoLink) {
      if (href && children && children.length) {
        return this.menuLinkAndParentTemplate(title, href, children, menuDepth);
      }
    }
    if (children && children.length) {
      return this.menuParentTemplate(title, children, menuDepth);
    }
    if (href) {
      return TuftsMenu.menuLinkTemplate(title, href, menuDepth);
    }
    return TuftsMenu.menuItemTemplate(title, menuDepth);
  }

  expandParents() {
    // Time out required for list to render
    setTimeout(() => {
      const currentPageElement = this.shadowRoot?.querySelector(
        `[href='${this.activePage}']`
      );
      if (currentPageElement) {
        currentPageElement.setAttribute('aria-current', 'page');
        let buttonToExpand = null;
        // If the current page is at the top level, expand the button next to it, otherwise find the closest ancestor button.
        if (currentPageElement.classList.contains('menu-link--level-0')) {
          buttonToExpand = currentPageElement.nextElementSibling;
        } else {
          buttonToExpand = this.findParentButton(currentPageElement);
        }
        // Cycle through expanding all dropdowns above the current location
        while (buttonToExpand) {
          buttonToExpand.setAttribute('aria-expanded', 'true');
          buttonToExpand = this.findParentButton(buttonToExpand);
        }
      }
    }, 0);
  }

  toggleHamburgerSideBar() {
    this.hamburgerSideBarOpen = !this.hamburgerSideBarOpen;
  }

  //This function is intentionally empty because we don't need it to be executed on the sidebar comp.
  closeBlurMenu() {}

  mobileTemplate() {
    return html`
      <div class="sidenav-wrapper">
        <details
          @click="${this.toggleHamburgerSideBar}"
          aria-expanded="${this.hamburgerSideBarOpen}"
        >
          <summary>
            In this section
            <tufts-icon
              name="${this.hamburgerSideBarOpen ? 'close-mobile' : 'duo-menu'}"
              size="1.25rem"
              library="system"
              class="dropdown-icon"
            ></tufts-icon>
          </summary>
          <nav
            class="${this.menuId}-menu sidebar"
            id="${this.menuId}-menu"
            aria-labelledby="${this.menuId}-menu-title"
          >
            <h2 class="visually-hidden" id="${this.menuId}-menu-title">
              ${this.menuName}
            </h2>
            ${this.renderMenuLevel(this.tree)}
          </nav>
        </details>
      </div>
    `;
  }
  desktopTemplate() {
    return html`
      <nav
        class="${this.menuId}-menu sidebar"
        id="${this.menuId}-menu"
        aria-labelledby="${this.menuId}-menu-title"
      >
        <h2 class="visually-hidden" id="${this.menuId}-menu-title">
          ${this.menuName}
        </h2>
        ${this.renderMenuLevel(this.tree)}
      </nav>
    `;
  }

  render() {
    if (this.isLoading) return null;
    // Don't display a menu if there is no data
    if (this.tree.length === 0) return null;
    this.expandParents();
    return this.isMobile === true
      ? this.mobileTemplate()
      : this.desktopTemplate();
  }
}
declare global {
  interface HTMLElementTagNameMap {
    'tufts-sidebar': TuftsSidebar;
  }
}
